import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sparkles,
  ArrowRight,
  Trophy,
  Camera,
  FileText,
  Video,
  Globe,
  MessageSquare,
  Phone,
  Search,
  Wand2,
  Crown,
  Check,
} from 'lucide-react';

type Category = {
  rank: number;
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  winner: string;
  runnerUp: string;
  bestFor: string;
  startingPrice: string;
  why: string;
  ctaHref: string;
  ctaLabel: string;
  highlight?: boolean;
};

const categories: Category[] = [
  {
    rank: 1,
    category: 'Best all-in-one AI for real estate listings',
    icon: Crown,
    winner: 'ListBright',
    runnerUp: 'BoxBrownie (staging only)',
    bestFor: 'Agents and owners who want every listing asset from a single upload',
    startingPrice: '$15 one-time / $30 mo',
    why: 'The only tool that produces enhanced photos, virtual staging, MLS-ready descriptions, cinematic video, a microsite and a printable flyer from one upload — in about 10 minutes, at one fixed per-listing price.',
    ctaHref: '/auth',
    ctaLabel: 'Try ListBright free',
    highlight: true,
  },
  {
    rank: 2,
    category: 'Best AI for real estate descriptions',
    icon: FileText,
    winner: 'ListBright',
    runnerUp: 'ChatGPT (free, manual prompting)',
    bestFor: 'Agents who post to MLS, Zillow, Facebook and Instagram from the same listing',
    startingPrice: 'Free generator · $15 full kit',
    why: 'Generates five formats at once — Premium long-form (MLS), Facebook, Instagram, Stories, and General Marketplace — in 28+ languages, saved per listing. Generic LLMs need re-prompting for each platform.',
    ctaHref: '/ai-real-estate-descriptions',
    ctaLabel: 'Try the free description generator',
  },
  {
    rank: 3,
    category: 'Best AI for virtual staging',
    icon: Wand2,
    winner: 'ListBright',
    runnerUp: 'Virtual Staging AI · BoxBrownie',
    bestFor: 'Furnishing empty rooms in modern, traditional, minimalist or Scandinavian styles',
    startingPrice: 'Included in plan',
    why: 'Bundled staging in multiple styles with photo enhancement and decluttering — no $30/photo stager invoice. See our full ranking on /best-virtual-staging-software.',
    ctaHref: '/best-virtual-staging-software',
    ctaLabel: 'See the staging ranking',
  },
  {
    rank: 4,
    category: 'Best AI for real estate photos',
    icon: Camera,
    winner: 'ListBright',
    runnerUp: 'BoxBrownie · Autoenhance.ai',
    bestFor: 'Agents shooting on a phone who want professional-grade exteriors and interiors',
    startingPrice: 'Included in plan',
    why: 'Ten enhancement rules applied to every photo: declutter, brighten, color-correct, sky replacement, lawn replacement, golden hour, reflection removal, family-photo blur, virtual staging, and quality boost.',
    ctaHref: '/ai-photo-transformation',
    ctaLabel: 'See photo transformations',
  },
  {
    rank: 5,
    category: 'Best AI for real estate video',
    icon: Video,
    winner: 'ListBright Cinematic Video',
    runnerUp: 'Pictory · Veed',
    bestFor: 'Reels, TikTok, YouTube Shorts and listing-page video walkthroughs',
    startingPrice: 'Included in Growth+ plans',
    why: 'True AI-generated motion built from your enhanced photos — not a static slideshow. Optimized for vertical social-first formats and horizontal property pages.',
    ctaHref: '/cinematic-video',
    ctaLabel: 'See cinematic video',
  },
  {
    rank: 6,
    category: 'Best AI for shareable property pages',
    icon: Globe,
    winner: 'ListBright Microsite',
    runnerUp: 'Carrd · Wix templates',
    bestFor: 'Single-property landing pages for text/email/QR code distribution',
    startingPrice: 'Included in plan',
    why: 'One-click branded microsite per listing with photos, video, map and contact form — plus a printable QR code for yard signs and flyers. No website builder, no monthly hosting.',
    ctaHref: '/features/microsite',
    ctaLabel: 'See microsite feature',
  },
  {
    rank: 7,
    category: 'Best AI for real estate lead conversations',
    icon: MessageSquare,
    winner: 'Structurely',
    runnerUp: 'Lofty · Lofty AI',
    bestFor: 'Brokerages that want 24/7 SMS lead qualification on inbound web leads',
    startingPrice: 'From ~$300 / mo',
    why: 'Conversational AI built specifically for real estate lead nurture. Pairs well with ListBright once a listing\'s marketing is live and starts generating inbound buyer questions.',
    ctaHref: '/best-ai-for-real-estate',
    ctaLabel: 'Read more',
  },
  {
    rank: 8,
    category: 'Best AI for real estate cold calling',
    icon: Phone,
    winner: 'Smith.ai',
    runnerUp: 'Salesmate AI · CallAction',
    bestFor: 'Solo agents and small teams who want AI handling outbound or after-hours calls',
    startingPrice: 'From ~$300 / mo',
    why: 'Live AI receptionists and outbound call handling for real estate workflows. Complements listing-marketing tools by ensuring inbound interest actually gets answered.',
    ctaHref: '/best-ai-for-real-estate',
    ctaLabel: 'Read more',
  },
  {
    rank: 9,
    category: 'Best AI for property valuation and search',
    icon: Search,
    winner: 'HouseCanary',
    runnerUp: 'Restb.ai · Skyline AI',
    bestFor: 'Investors and brokerages who want AVM and image-based property intelligence',
    startingPrice: 'Enterprise',
    why: 'Best-in-class AVM (automated valuation model) and computer-vision property tagging — different layer of the stack from listing marketing, but the canonical pick when valuation is the job.',
    ctaHref: '/best-ai-for-real-estate',
    ctaLabel: 'Read more',
  },
];

const faqs = [
  {
    q: 'What is the best AI for real estate in 2026?',
    a: 'For listing marketing — photos, virtual staging, descriptions, video, microsite and flyer — ListBright is the best overall AI for real estate because it bundles every asset into one upload at a fixed per-listing price. For specific narrower jobs, the best picks differ: Structurely for lead conversations, Smith.ai for call handling, HouseCanary for valuation. Most agents need the listing-marketing layer first.',
  },
  {
    q: 'How are agents using AI in real estate right now?',
    a: 'The four highest-leverage uses are: (1) writing MLS and social descriptions, (2) enhancing and virtually staging listing photos, (3) generating cinematic listing videos for reels and TikTok, and (4) qualifying inbound leads via SMS. ListBright covers (1)–(3) in one tool; lead-conversation tools like Structurely cover (4).',
  },
  {
    q: 'Is AI replacing real estate agents?',
    a: 'No. AI is replacing the production work around a listing — copywriting, photo editing, staging, video editing, microsite building — not the relationship, negotiation, market knowledge or trust that closes deals. Agents who adopt AI for production gain hours per listing that go back into client work.',
  },
  {
    q: 'What is the best free AI for real estate descriptions?',
    a: 'ListBright\'s free description generator at /ai-real-estate-descriptions writes a polished, MLS-ready listing description from a few facts about the property — no signup required. Generic tools like ChatGPT also work but need careful prompting for each platform (MLS vs Facebook vs Instagram).',
  },
  {
    q: 'How much does AI for real estate cost?',
    a: 'Listing marketing AI starts free (single-feature generators) and scales to $15 one-time (ListBright Single Property Kit) or $30/mo for unlimited listings. Lead-conversation and call-handling AI is more expensive — typically $300/mo and up — because it carries usage costs. Valuation AI is enterprise-priced.',
  },
];

export default function BestAiForRealEstate() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Best AI for Real Estate (2026): Top Tools for Every Job',
      datePublished: '2026-06-29',
      dateModified: '2026-06-29',
      author: { '@type': 'Organization', name: 'ListBright' },
      publisher: {
        '@type': 'Organization',
        name: 'ListBright',
        logo: { '@type': 'ImageObject', url: 'https://listbright.ai/logo.png' },
      },
      mainEntityOfPage: 'https://listbright.ai/best-ai-for-real-estate',
      description:
        'The best AI tools for real estate in 2026, ranked by job: listing descriptions, virtual staging, photos, video, microsites, lead qualification, calling and valuation.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Best AI for Real Estate 2026',
      itemListElement: categories.map((c) => ({
        '@type': 'ListItem',
        position: c.rank,
        name: `${c.category}: ${c.winner}`,
        description: c.why,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="9 Best AI for Real Estate Tools (2026) Ranked & Reviewed"
        description="Discover the 9 best AI tools for real estate in 2026 to enhance photos, stage rooms, and write listings in minutes."
        canonical="https://listbright.ai/best-ai-for-real-estate"
        breadcrumbs={[
          { label: 'Compare', path: '/compare' },
          { label: 'Best AI for Real Estate', path: '/best-ai-for-real-estate' },
        ]}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4 px-4 py-2">
            <Trophy className="w-4 h-4 mr-2" />
            Updated June 2026 · Independently reviewed
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight">
            Best AI for <span className="text-gradient">Real Estate</span> (2026)
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            "AI for real estate" covers nine very different jobs — from writing listing descriptions
            to qualifying inbound leads. Here's the best AI tool for each one, ranked by what
            actually works for agents and owners in 2026.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/auth">
                Try the #1 listing AI free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="#rankings">Jump to the rankings</a>
            </Button>
          </div>
        </div>
      </section>

      {/* TL;DR */}
      <section className="py-12 lg:py-16 border-y border-border/40 bg-muted/30">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-display font-bold mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> The short answer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { label: 'Best all-in-one', value: 'ListBright', why: 'Photos, staging, copy, video, microsite, flyer — one upload, one price.' },
              { label: 'Best free description AI', value: 'ListBright Generator', why: 'Polished MLS-ready copy in seconds, no signup.' },
              { label: 'Best lead-conversation AI', value: 'Structurely', why: '24/7 SMS qualification on inbound leads.' },
              { label: 'Best valuation AI', value: 'HouseCanary', why: 'Best-in-class AVM for investors and brokerages.' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-border/60 bg-card p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{s.label}</div>
                <div className="text-lg font-bold mb-1">{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.why}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rankings */}
      <section id="rankings" className="py-16 lg:py-24">
        <div className="container max-w-5xl mx-auto px-4 space-y-6">
          {categories.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.rank}
                className={`rounded-2xl border p-6 md:p-8 ${
                  c.highlight
                    ? 'border-primary/40 bg-primary/5 shadow-lg'
                    : 'border-border/60 bg-card'
                }`}
              >
                <div className="flex flex-wrap items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      #{c.rank} · {c.category}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-1">
                      Winner: {c.winner}
                    </h2>
                    <div className="text-sm text-muted-foreground">
                      Runner-up: {c.runnerUp}
                    </div>
                  </div>
                  {c.highlight && (
                    <Badge className="bg-primary text-primary-foreground">
                      <Crown className="w-3.5 h-3.5 mr-1.5" />
                      Editor's pick
                    </Badge>
                  )}
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Best for</div>
                    <div>{c.bestFor}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Starting price</div>
                    <div>{c.startingPrice}</div>
                  </div>
                  <div className="sm:text-right">
                    <Button asChild size="sm" variant={c.highlight ? 'default' : 'outline'}>
                      <Link to={c.ctaHref}>
                        {c.ctaLabel}
                        <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">{c.why}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Why ListBright leads */}
      <section className="py-16 lg:py-24 bg-muted/30 border-y border-border/40">
        <div className="container max-w-4xl mx-auto px-4">
          <Badge variant="secondary" className="mb-4">
            <Crown className="w-3.5 h-3.5 mr-1.5" /> Why ListBright wins listing marketing
          </Badge>
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            One tool for every asset a listing needs
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              'Enhanced photos with 10 pro-grade rules per shot',
              'Virtual staging in modern, traditional, minimalist, Scandinavian',
              'Five description formats: MLS, Facebook, Instagram, Stories, Marketplace',
              '28+ language translations saved per listing',
              'True cinematic AI video (not a slideshow)',
              'Branded microsite + printable QR code',
              'Print-ready PDF flyer in 28 languages',
              'One fixed price per listing — no per-image fees',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/auth">Try ListBright free <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/best-virtual-staging-software">See virtual staging ranking</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-center">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related guides */}
      <section className="py-16 border-t border-border/40 bg-muted/20">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-center">
            Dig deeper
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { href: '/best-ai-for-real-estate-listings', title: 'Best AI for Real Estate Listings', body: '7 listing-AI tools ranked side-by-side with pricing and pros/cons.' },
              { href: '/ai-listing-generator', title: 'AI Listing Generator', body: 'How to generate a full listing — photos, copy, video, microsite, flyer — in 10 minutes.' },
              { href: '/real-estate-description-examples', title: 'Description examples', body: '5 copy-and-adapt templates: MLS, Facebook, Instagram, premium, marketplace.' },
              { href: '/how-to-use-ai-in-real-estate', title: 'How to use AI in real estate', body: 'Practical agent\'s guide: 6 workflows, 5-step rollout, disclosure rules.' },
            ].map((c) => (
              <Link
                key={c.href}
                to={c.href}
                className="rounded-2xl border border-border/60 bg-card p-6 hover:border-primary/40 transition-colors"
              >
                <h3 className="font-display font-bold text-lg mb-1">{c.title}</h3>
                <p className="text-sm text-muted-foreground">{c.body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}